import { Box, Chip, Stack, Typography, IconButton } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

dayjs.extend(utc);
dayjs.extend(timezone);

// AutoPay start date must land on the same calendar day the backend treats as
// "today" (validateAutoPayDates checks against America/New_York), regardless
// of the customer's local browser timezone.
const AUTO_PAY_TIMEZONE = 'America/New_York';
const getAuthorizationDate = (): Dayjs => dayjs().tz(AUTO_PAY_TIMEZONE);

import { getBankAccountsList, BankAccountApiResponse } from '../../services/bank-account-services';
import { getProfileDetails } from '../../services/user-service';
import { RootState } from '../../store';
import { UserDetails } from '../../store/reducers/userSlice';
import { useTranslation } from '../../utils/translations';
import ButtonComponent from '../button/ButtonComponent';
import { CustomCheckBox } from '../checkBox/CustomCheckBox';
import { CommonModal } from '../modal/modal';
import { CustomSelect } from '../select/CustomSelect';
import { TextFieldComponent } from '../text-field';
import { colors } from '../theme/theme';
import { BrandLogo } from '../ui-components/BrandLogo';

export type AutoPayFormType = 'consumer-loan' | 'consumer-lease' | 'commercial-loan' | 'commercial-lease';

// Shared boilerplate paragraphs identical across the commercial-lease and consumer-lease
// authorization disclosure.
const LeaseAuthorizationSharedParagraphs: React.FC = () => (
    <>
        <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
            Lessee agrees to maintain a balance in the Account sufficient to cover such monthly amounts. In the event
            any withdrawal made under this Authorization is not paid upon presentation, Lessor reserves the right to
            cancel this Authorization and require Lessee to remit all monthly payments and other sums due and payable
            under the terms of the Agreement directly to Lessor. Lessee acknowledges and agrees that all payments due
            under the terms of the Agreement are the responsibility of Lessee. If any payment is not made due to the
            temporary suspension of service or cancellation or because of insufficient funds in the Account, Lessor may
            at its discretion attempt to process the payment again and Lessee agrees to additional fees, which will be
            initiated as a separate transaction from the monthly payment. If Lessor is unable to process payments under
            this Authorization, Lessee shall be required to make the payment manually on time. If this is not done, late
            charges (as defined in the Agreement) shall apply.
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
            Lessee agrees not to dispute these scheduled transactions with its bank provided the transactions correspond
            to the terms indicated in this Authorization.
        </Typography>
    </>
);

// Prefers the username, falling back to the display name; shared by the effects that sync the
// borrower field from the logged-in user's details. Takes the two primitive fields directly
// (rather than the whole userDetails object) so callers can keep narrow useEffect dependency
// arrays that only re-run when Username/Name actually change.
const getBorrowerDisplayName = (username?: string, name?: string): string => {
    if (username) {
        return username;
    }
    if (name) {
        return name;
    }
    return '';
};

// Impersonating: userDetails starts as a placeholder (Id null, Name 'Super User'), and that Name stays in
// state after the impersonated user's profile is merged in, so Name can't be trusted here. Use only real
// name keys from the loaded profile (first + last name, then Username); otherwise show nothing rather than
// a dummy name.
const getImpersonatedBorrowerName = (userDetails: UserDetails | null | undefined): string => {
    if (userDetails?.Id == null) {
        return '';
    }
    const fullName = `${userDetails.FirstName ?? ''} ${userDetails.LastName ?? ''}`.trim();
    return fullName || userDetails.Username || '';
};

interface AutoPayAuthorizationModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (formData: AutoPayFormData) => void;
    formType?: AutoPayFormType;
    agreementNumber?: string;
    /** When no bank accounts, clicking Add Account calls this; parent should close modal, open add-bank flow, then reopen modal after add. */
    onAddBankAccountRequest?: () => void;
    /** When admin is impersonating a user */
    isImpersonating?: boolean;
}

export interface AutoPayFormData {
    accountType: string;
    agreeToTerms: boolean;
    borrower: string;
    formType: AutoPayFormType;
    startDate?: Dayjs | null;
    bankAccount?: BankAccountApiResponse;
    pdfFile?: File;
    pdfBase64?: string;
}

const AutoPayAuthorizationModal: React.FC<AutoPayAuthorizationModalProps> = ({
    open,
    onClose,
    onSubmit,
    formType = 'consumer-lease',
    onAddBankAccountRequest,
    isImpersonating = false,
}) => {
    const { t } = useTranslation();
    const storeUserDetails = useSelector((state: RootState) => state.user.userDetails);
    // While impersonating, load the impersonated user's profile directly for this form instead of relying on
    // the shared store being populated/not reset by the time the form opens.
    const [impersonatedProfile, setImpersonatedProfile] = useState<UserDetails | null>(null);
    const userDetails = isImpersonating && impersonatedProfile ? impersonatedProfile : storeUserDetails;
    const [formData, setFormData] = useState<AutoPayFormData>({
        accountType: '',
        agreeToTerms: false,
        borrower: '',
        formType: formType,
        startDate: getAuthorizationDate(),
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [bankAccounts, setBankAccounts] = useState<BankAccountApiResponse[]>([]);
    const [bankAccountOptions, setBankAccountOptions] = useState<Array<{ id: string; name: string }>>([]);
    const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccountApiResponse | null>(null);

    // Update formType when prop changes
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setFormData(prev => ({
            ...prev,
            formType: formType,
        }));
    }, [formType]);

    const fetchBankAccounts = useCallback(async () => {
        try {
            const response = await getBankAccountsList(true);
            const data = response?.statusCode === 200 && Array.isArray(response?.data) ? response.data : [];
            setBankAccounts(data);

            const activeAccounts = data.filter(
                (acc: BankAccountApiResponse) => acc?.IsActive && acc?.AspireIsActive !== false,
            );
            const options = activeAccounts.map((acc: BankAccountApiResponse) => ({
                id: acc.Id,
                name: `${acc.BankName ?? ''} (****${(acc.AccountNumber ?? '').slice(-4)} - ${acc.AccountType})${acc.NickName ? ` - ${acc.NickName}` : ''}`,
            }));
            setBankAccountOptions(options);

            if (options.length > 0 && activeAccounts.length > 0) {
                const firstActive = activeAccounts[0];
                setFormData(prev => ({
                    ...prev,
                    accountType: firstActive.Id,
                    formType: formType,
                    startDate: prev.startDate || getAuthorizationDate(),
                }));
                setSelectedBankAccount(firstActive);
            } else {
                setFormData(prev => ({
                    ...prev,
                    accountType: '',
                    formType: formType,
                    startDate: prev.startDate || getAuthorizationDate(),
                }));
                setSelectedBankAccount(null);
            }
        } catch (error: unknown) {
            console.error('Error fetching bank accounts:', error);
            setBankAccounts([]);
            setBankAccountOptions([]);
            setSelectedBankAccount(null);
            setFormData(prev => ({ ...prev, accountType: '' }));
        }
    }, [formType]);

    // Fetch bank accounts when modal opens
    useEffect(() => {
        if (open) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            fetchBankAccounts();
        }
    }, [open, fetchBankAccounts]);

    useEffect(() => {
        if (!open || !isImpersonating) {
            return;
        }
        let cancelled = false;
        getProfileDetails(false)
            .then(response => {
                const profile = response?.data?.data || response?.data;
                if (!cancelled && profile && typeof profile === 'object') {
                    setImpersonatedProfile(profile as UserDetails);
                }
            })
            .catch(error => console.error('Error fetching impersonated user details for AutoPay form:', error));
        return () => {
            cancelled = true;
        };
    }, [open, isImpersonating]);

    // Shared body for the two effects below: both just re-apply the borrower name derived from
    // userDetails, they only differ in what triggers them (see each effect's comment).
    const applyBorrowerFromUserDetails = () => {
        // While impersonating, userDetails starts as a 'Super User' placeholder (see getImpersonatedBorrowerName).
        const displayName = isImpersonating
            ? getImpersonatedBorrowerName(userDetails)
            : getBorrowerDisplayName(userDetails?.Username, userDetails?.Name);
        setFormData(prev => ({
            ...prev,
            borrower: displayName,
        }));
    };

    // Sync borrower name from userDetails when userDetails loads
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        applyBorrowerFromUserDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDetails, isImpersonating]);

    // When modal opens, re-apply borrower from userDetails (in case it was cleared on previous close)
    useEffect(() => {
        if (open) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            applyBorrowerFromUserDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        open,
        userDetails?.Username,
        userDetails?.Name,
        userDetails?.Id,
        userDetails?.FirstName,
        userDetails?.LastName,
        isImpersonating,
    ]);

    const handleChange = (field: keyof AutoPayFormData, value: AutoPayFormData[keyof AutoPayFormData]) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }

        // Update selected bank account when accountType changes
        if (field === 'accountType' && value) {
            const bankAccount = bankAccounts.find(acc => acc.Id === value);
            setSelectedBankAccount(bankAccount || null);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.accountType) {
            newErrors.accountType = t('autoPay.validation.accountTypeRequired');
        }
        if (!formData.startDate) {
            newErrors.startDate = t('autoPay.validation.startDateRequired');
        }
        if (!formData.agreeToTerms) {
            newErrors.agreeToTerms = t('autoPay.validation.agreeToTermsRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onSubmit({
                ...formData,
                bankAccount: selectedBankAccount || undefined,
            });
        }
    };

    const handleCancel = () => {
        setFormData({
            accountType: '',
            agreeToTerms: false,
            borrower: '',
            formType: formType,
            startDate: getAuthorizationDate(),
        });
        setErrors({});
        setSelectedBankAccount(null);
        onClose();
    };

    // Prevent closing on backdrop click
    const handleCloseModal = (_event: object, reason?: string) => {
        // Only allow closing via explicit cancel button or close icon, not backdrop click
        if (reason === 'backdropClick') {
            return;
        }
        handleCancel();
    };

    const isSubmitDisabled =
        isImpersonating ||
        bankAccountOptions?.length === 0 ||
        !formData.accountType ||
        !formData.startDate ||
        !!errors.startDate ||
        !formData.agreeToTerms;

    return (
        <CommonModal
            modalOpen={open}
            onCloseModal={() => handleCloseModal({})}
            primaryBtnClick={handleCancel}
            secondaryBtnClick={handleSubmit}
            primaryBtnName={t('autoPay.buttons.cancel')}
            secondaryBtnName={t('autoPay.buttons.submit')}
            secondaryBtnDisabled={isSubmitDisabled}
            ModalMaxWidth={700}
            sx={{
                '& .MuiDialogContent-root': {
                    // maxHeight: '70vh',
                    overflowY: 'auto',
                },
                '& .footer-actions': {
                    justifyContent: 'flex-end',
                    '& .MuiButtonBase-root': {
                        width: 'fit-content',
                    },
                },
            }}
        >
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
                <Stack>
                    <BrandLogo alt="logo" style={{ height: '100%', width: '100%', maxHeight: 60, maxWidth: 188 }} />
                </Stack>
                <IconButton onClick={handleCancel} aria-label="Close">
                    <span className="icon-close" style={{ color: colors.fullBlack, fontSize: 24 }} />
                </IconButton>
            </Stack>
            <Stack sx={{ maxHeight: 'calc(100vh - 270px)', overflow: 'auto' }}>
                <Stack
                    sx={{
                        border: `1px solid ${colors.greyPreset[30]}`,
                        borderRadius: 2,
                        p: 2,
                    }}
                >
                    <Stack mb={1.5}>
                        <Typography variant="subtitle1" color={colors.primaryLight[100]} sx={{ mb: 0.5 }}>
                            {t('autoPay.title')}
                        </Typography>
                    </Stack>
                    <Stack spacing={2}>
                        <Stack gap={1.5}>
                            {formType === 'consumer-loan' && (
                                <>
                                    {/* prettier-ignore */}
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        This Authorization for Automatic Withdrawal (&quot;Authorization&quot;) given by Borrower
                                        authorizes Lender to withdraw funds from and/or to debit authorizes Lender to
                                        withdraw funds from and/or to debit the account provided by the Borrower
                                        (&quot;Account&quot;) in the amount of the monthly charges which may include, in addition
                                        to regular monthly principal and interest payments, taxes or other fees incurred
                                        from time to time on the above referenced Agreement (&quot;Agreement&quot;). This
                                        withdrawal will be made monthly on the due date specified in the Agreement. This
                                        Authorization will remain in full force until Borrower has notified Lender in
                                        writing at least 3 business days in advance of its decision to terminate or
                                        suspend this Authorization or until all amounts due under the Agreement are paid
                                        in full. Borrower further acknowledges and agrees that suspension or termination
                                        of this Authorization will not relieve the Borrower of its obligation to make
                                        payments to Lender.
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        Borrower agrees to maintain a balance in the Account sufficient to cover such
                                        monthly amounts. In the event any withdrawal made under this Authorization is
                                        not paid upon presentation, Lender reserves the right to cancel this
                                        Authorization and require Borrower to remit all monthly payments and other sums
                                        due and payable under the terms of the Agreement directly to Lender. Borrower
                                        acknowledges and agrees that all payments due under the terms of the Agreement
                                        are the responsibility of Borrower. If any payment is not made due to the
                                        temporary suspension of service or cancellation or because of insufficient funds
                                        in the Account, Lender may at its discretion attempt to process the payment
                                        again and Borrower agrees to additional fees, which will be initiated as a
                                        separate transaction from the monthly payment. If Lender is unable to process
                                        payments under this Authorization, Borrower shall be required to make the
                                        payment manually on time. If this is not done, late charges (as defined in the
                                        Agreement) shall apply.
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        Borrower agrees not to dispute these scheduled transactions with its bank
                                        provided the transactions correspond to the terms indicated in this
                                        Authorization.
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        Borrower agrees that any failure to initiate or settle a withdrawal on the due
                                        date specified in the Agreement is neither a waiver of the right to do so, nor a
                                        waiver or modification of any terms of the Agreement.
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        The below-signed individual certifies that he/she is the Borrower or is an
                                        authorized agent of the Borrower with the authority to execute this instrument,
                                        the bank and account information below is accurate and complete, the bank
                                        indicated below is a member of the ACH network and Borrower is the owner and/or
                                        an authorized user of the account. The Borrower agrees that Lender and third
                                        parties, including the below-designated bank, may rely upon this instrument as
                                        being valid and in full force and effect, absent express notice otherwise.
                                    </Typography>
                                </>
                            )}

                            {formType === 'commercial-loan' && (
                                <>
                                    {/* prettier-ignore */}
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        This Authorization for Automatic Withdrawal (&quot;Authorization&quot;) given by Borrower
                                        authorizes Lender to withdraw funds from and/or to debit authorizes Lender to
                                        withdraw funds from and/or to debit the account provided by the Borrower
                                        (&quot;Account&quot;) in the amount of the monthly charges which may include, in addition
                                        to regular monthly principal and interest payments, interim interest, taxes or
                                        other fees incurred from time to time on the above referenced Agreement
                                        (&quot;Agreement&quot;). This withdrawal will be made on the due date specified in the
                                        Agreement. This Authorization will remain in full force until Borrower has
                                        notified Lender in writing at least 30 days in advance of its decision to
                                        terminate or suspend this Authorization or until all amounts due under the
                                        Agreement are paid in full. Borrower further acknowledges and agrees that
                                        suspension or termination of this Authorization will not relieve the Borrower or
                                        its guarantors of its obligation to make payments to Lender. Suspension or
                                        termination of this Agreement will constitute an automatic default on the
                                        Agreement or any other financing agreements with the Lender in the event this
                                        Authorization was a credit approval requirement.
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        Borrower agrees to maintain a balance in the Account sufficient to cover such
                                        monthly amounts. In the event any withdrawal made under this Authorization is
                                        not paid upon presentation, Lender reserves the right to cancel this
                                        Authorization and requires Borrower to remit all monthly payments and other sums
                                        due and payable under the terms of the Agreement directly to Lender. Borrower
                                        acknowledges and agrees that all payments due under the terms of the Agreement
                                        are the responsibility of Borrower. If any payment is not made due to the
                                        temporary suspension of service or cancellation or because of insufficient funds
                                        in the Account, Lender may at its discretion attempt to process the payment
                                        again and Borrower agrees to additional fees, which will be initiated as a
                                        separate transaction from the monthly payment. If Lender is unable to process
                                        payments under this Authorization, Borrower shall be required to make the
                                        payment manually on time. If this is not done, late charges (as defined in the
                                        Agreement) shall apply.
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        Borrower agrees not to dispute these scheduled transactions with its bank
                                        provided the transactions correspond to the terms indicated in this
                                        Authorization.
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        Signature below indicates that Borrower has verified and confirmed that all the
                                        information provided above is correct.
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        sx={{ fontWeight: 600, lineHeight: '16px' }}
                                    >
                                        Borrower:
                                        <Typography
                                            variant="caption"
                                            component="span"
                                            sx={{ ml: 0.5, lineHeight: '16px' }}
                                        >
                                            {formData.borrower}
                                        </Typography>
                                    </Typography>
                                </>
                            )}

                            {formType === 'commercial-lease' && (
                                <>
                                    {/* prettier-ignore */}
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        This Authorization For Automatic Withdrawal (&quot;Authorization&quot;) given by Lessee
                                        authorizes Lessor to withdraw funds from and/or to debit authorizes Lessor to
                                        withdraw funds from and/or to debit the account provided by the Lessee
                                        (&quot;Account&quot;) in the amount of the monthly charges which may include, in addition
                                        to regular monthly payments, interim rent, taxes or other fees incurred from
                                        time to time on the above referenced Agreement (&quot;Agreement&apos;). This withdrawal
                                        will be made on the due date specified in the Agreement. This Authorization will
                                        remain in full force until Lessee has notified Lessor in writing at least 30
                                        days in advance of its decision to terminate or suspend this Authorization or
                                        until all amounts due under the Agreement are paid in full. Lessee further
                                        acknowledges and agrees that suspension or termination of this Authorization
                                        will not relieve the Lessee or its guarantors of its obligation to make payments
                                        to Lessor. Suspension or termination of this Agreement will constitute an
                                        automatic default on the Agreement or any other financing agreements with the
                                        Lessor in the event this Authorization was a credit approval requirement.
                                    </Typography>
                                    <LeaseAuthorizationSharedParagraphs />
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        Signature below indicates that Lessee has verified and confirmed that all of the
                                        information provided above is correct.
                                    </Typography>
                                </>
                            )}

                            {formType === 'consumer-lease' && (
                                <>
                                    {/* prettier-ignore */}
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        This Authorization for Automatic Withdrawal (&quot;Authorization&quot;) given by Lessee
                                        authorizes Lessor to withdraw funds from and/or to debit authorizes Lessor to
                                        withdraw funds from and/or to debit the account provided by the Lessee
                                        (&quot;Account&quot;) in the amount of the monthly charges which may include, in addition
                                        to regular monthly principal and interest payments, taxes or other fees incurred
                                        from time to time on the above referenced Agreement (&quot;Agreement&quot;). This
                                        withdrawal will be made monthly on the due date specified in the Agreement. This
                                        Authorization will remain in full force until Lessee has notified Lessor in
                                        writing at least 3 business days in advance of its decision to terminate or
                                        suspend this Authorization or until all amounts due under the Agreement are paid
                                        in full. Lessee further acknowledges and agrees that suspension or termination
                                        of this Authorization will not relieve the Lessee of its obligation to make
                                        payments to Lessor.
                                    </Typography>
                                    <LeaseAuthorizationSharedParagraphs />
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        Lessee agrees that any failure to initiate or settle a withdrawal on the due
                                        date specified in the Agreement is neither a waiver of the right to do so, nor a
                                        waiver or modification of any terms of the Agreement.
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: '16px' }}>
                                        The below-signed individual certifies that he/she is the Lessee or is an
                                        authorized agent of the Lessee with the authority to execute this instrument,
                                        the bank and account information below is accurate and complete, the bank
                                        indicated below is a member of the ACH network and Lessee is the owner and/or an
                                        authorized user of the account. The Lessee agrees that Lessor and third parties,
                                        including the below-designated bank, may rely upon this instrument as being
                                        valid and in full force and effect, absent express notice otherwise.
                                    </Typography>
                                </>
                            )}
                        </Stack>
                        {/* Bank Details Section */}
                        <Box>
                            <Typography variant="subtitle1" color={colors.primaryLight[100]} mb={1.25}>
                                {t('autoPay.bankDetails')}
                            </Typography>
                            <Stack
                                sx={{
                                    gap: 1.5,
                                    border: `1px solid ${colors.greyPreset[30]}`,
                                    borderRadius: 2,
                                    p: 1.5,
                                }}
                            >
                                <Box maxWidth={300}>
                                    {bankAccountOptions?.length === 0 ? (
                                        <Stack gap={1}>
                                            <Typography variant="caption" color="textSecondary">
                                                {t('autoPay.pleaseAddBankAccount')}
                                            </Typography>
                                            <ButtonComponent
                                                variant="contained"
                                                onClick={() => {
                                                    onClose();
                                                    onAddBankAccountRequest?.();
                                                }}
                                                endIcon={<span className="icon-plus" />}
                                                sx={{ width: 'fit-content' }}
                                            >
                                                {t('BankAccounts.addAccount')}
                                            </ButtonComponent>
                                        </Stack>
                                    ) : (
                                        <>
                                            <CustomSelect
                                                id="autopay-bank-account-select"
                                                ariaLabel={t('autoPay.bankAccount')}
                                                labelSrOnly
                                                title={t('autoPay.bankAccount')}
                                                required
                                                value={formData.accountType}
                                                onChange={value => handleChange('accountType', value)}
                                                options={bankAccountOptions}
                                                placeholder={t('autoPay.selectBankAccount')}
                                                noOptionsMessage={t('autoPay.pleaseAddBankAccount')}
                                                errorValue={errors.accountType}
                                                sx={{
                                                    '& .MuiOutlinedInput-input': { fontSize: '0.85rem' },
                                                    '& .MuiMenuItem-root': { fontSize: '0.85rem' },
                                                }}
                                            />
                                        </>
                                    )}
                                </Box>
                                {selectedBankAccount && (
                                    <Stack
                                        sx={{
                                            mt: 2,
                                            display: 'grid',
                                            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                                        }}
                                    >
                                        <Stack gap={0.5}>
                                            <Typography variant="body2" color="textSecondary">
                                                {t('autoPay.bankName')}
                                            </Typography>
                                            <Typography variant="subtitle1" color={colors.primaryLight[100]}>
                                                {selectedBankAccount.BankName}
                                            </Typography>
                                        </Stack>
                                        <Stack gap={0.5}>
                                            <Typography variant="body2" color="textSecondary">
                                                {t('autoPay.routingNumber')}
                                            </Typography>
                                            <Typography variant="subtitle1" color={colors.primaryLight[100]}>
                                                {selectedBankAccount.IdentificationNumber}
                                            </Typography>
                                        </Stack>
                                        <Stack gap={0.5}>
                                            <Typography variant="body2" color="textSecondary">
                                                {t('autoPay.accountNumber')}
                                            </Typography>
                                            <Typography variant="subtitle1" color={colors.primaryLight[100]}>
                                                {selectedBankAccount.AccountNumber}
                                            </Typography>
                                        </Stack>
                                        <Stack gap={0.5}>
                                            <Typography variant="body2" color="textSecondary">
                                                Account Type
                                            </Typography>
                                            <Chip
                                                label={selectedBankAccount.AccountType}
                                                size="small"
                                                sx={{
                                                    borderRadius: '12px',
                                                    fontWeight: 500,
                                                    color: colors.fullWhite,
                                                    backgroundColor:
                                                        selectedBankAccount.AccountType === 'Checking'
                                                            ? colors.bluePreset[60]
                                                            : colors.warningPreset[80],
                                                }}
                                            />
                                        </Stack>
                                    </Stack>
                                )}
                            </Stack>
                        </Box>
                        <Box maxWidth={300}>
                            <TextFieldComponent
                                label="Borrower Name"
                                value={formData.borrower}
                                onChange={() => {}}
                                errorValue={errors.borrower}
                                required
                                disabled={true}
                            />
                        </Box>
                        {/* Authorization Date */}
                        <Stack gap={0.75}>
                            <Box maxWidth={300}>
                                <TextFieldComponent
                                    label="Authorization Date"
                                    value={formData.startDate ? formData.startDate.format('MM/DD/YYYY') : ''}
                                    onChange={() => {}}
                                    disabled
                                />
                            </Box>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                            >
                                This is the date you are authorizing AutoPay enrollment. Payments will always be drafted
                                on your contract due date.
                            </Typography>
                        </Stack>
                        {/* Authorization Checkbox */}
                        <Stack gap={1.5}>
                            <Stack
                                component="label"
                                direction={'row'}
                                alignItems={'center'}
                                gap={1}
                                sx={{ cursor: 'pointer' }}
                            >
                                <CustomCheckBox
                                    id="autopay-agree-terms"
                                    checked={formData.agreeToTerms}
                                    onChange={e => handleChange('agreeToTerms', e.target.checked)}
                                    borderColor={errors.agreeToTerms ? colors.errorPreset[10] : colors.greyPreset[30]}
                                    checkedBorderColor={colors.warningPreset[30]}
                                />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: colors.primaryLight[100],
                                    }}
                                >
                                    {t('autoPay.agreeToTerms')}
                                </Typography>
                            </Stack>
                            {errors.agreeToTerms && (
                                <Typography variant="subtitle2" sx={{ color: colors.errorPreset[10], ml: 4 }}>
                                    {errors.agreeToTerms}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
                {isImpersonating && (
                    <Stack
                        sx={{
                            mt: 2,
                            p: 1.5,
                            backgroundColor: colors.warningPreset[40],
                            borderRadius: 1,
                            border: `1px solid ${colors.warningPreset[30]}`,
                        }}
                    >
                        <Typography variant="body2" color={colors.primaryLight[100]}>
                            {t('autoPay.impersonatingNote') ||
                                'The submit button is disabled while impersonating. Review the form but cannot submit while impersonating.'}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </CommonModal>
    );
};

export default AutoPayAuthorizationModal;
